import React, { useState, useEffect } from 'react';
import { API_LINK } from '../../apiLink/apiLink';
import Layout from '../../components/Layout';

const TeacherTimetable = ({ onNavigate, userInfo }) => {
  const [isMobile, setIsMobile] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fadeIn, setFadeIn] = useState(false);
  const [selectedDay, setSelectedDay] = useState('Monday'); // For mobile day selector

  // API Data States
  const [timetableData, setTimetableData] = useState({});
  const [teacherData, setTeacherData] = useState({
    name: "Loading...",
    teacherId: "...",
    department: "Computer Science",
    designation: "Lecturer",
    profileImage: null
  });

  // Dynamic time slots - no longer needed but kept for potential future use
  const [timeSlots, setTimeSlots] = useState([]);

  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Function to normalize time slot formats
  const normalizeTimeSlot = (timeSlot) => {
    if (!timeSlot) return '';

    // Remove any am/pm and extra spaces, convert to consistent format
    return timeSlot
      .toLowerCase()
      .replace(/am|pm/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  };

  // Function to extract unique time slots from API data
  const extractTimeSlots = (timetableData) => {
    const allTimeSlots = [];

    Object.values(timetableData).forEach(dayClasses => {
      dayClasses.forEach(classInfo => {
        if (classInfo.timeSlot && !allTimeSlots.includes(classInfo.timeSlot)) {
          allTimeSlots.push(classInfo.timeSlot);
        }
      });
    });

    // Sort time slots chronologically
    return allTimeSlots.sort((a, b) => {
      const timeA = normalizeTimeSlot(a).split(' - ')[0];
      const timeB = normalizeTimeSlot(b).split(' - ')[0];

      const convertTo24Hour = (time) => {
        // Handle different time formats
        let cleanTime = time.replace(/[^\d:]/g, '');
        if (!cleanTime.includes(':')) {
          // If no colon, assume it's just hours
          cleanTime = cleanTime + ':00';
        }

        const [hours, minutes] = cleanTime.split(':');
        let hour = parseInt(hours) || 0;
        let min = parseInt(minutes) || 0;

        // Convert to minutes for comparison
        return hour * 60 + min;
      };

      return convertTo24Hour(timeA) - convertTo24Hour(timeB);
    });
  };

  // Fetch Timetable Data from API
  useEffect(() => {
    const fetchTimetableData = async () => {
      try {
        setLoading(true);
        setError(null);

        console.log('🔍 Starting API call to fetch teacher timetable data...');

        // Get token from storage
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        console.log('🔑 Token found:', token ? 'Yes' : 'No');

        if (!token) {
          console.log('❌ No token found');
          setError('Authentication token not found. Please login again.');
          setLoading(false);
          return;
        }

        // Fetch teacher profile data first
        const profileResponse = await fetch(`${API_LINK}/api/teacher/profile`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (profileResponse.ok) {
          const profileResult = await profileResponse.json();
          if (profileResult.success && profileResult.data) {
            setTeacherData({
              name: profileResult.data.name,
              teacherId: profileResult.data.teacherId,
              department: profileResult.data.department,
              designation: profileResult.data.designation,
              profileImage: profileResult.data.profileImage
            });
          }
        }

        // Fetch timetable schedule data
        const scheduleResponse = await fetch(`${API_LINK}/api/teacher/schedule`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        console.log('📡 Schedule Response status:', scheduleResponse.status);
        console.log('📡 Schedule Response ok:', scheduleResponse.ok);

        if (!scheduleResponse.ok) {
          if (scheduleResponse.status === 401) {
            console.log('🔐 Authentication failed');
            setError('Session expired. Please login again.');
            handleLogout();
            return;
          } else if (scheduleResponse.status === 404) {
            console.log('🔍 API endpoint not found');
            setError('API endpoint not found. Please check server configuration.');
            setLoading(false);
            return;
          }
          throw new Error(`HTTP error! status: ${scheduleResponse.status}`);
        }

        const scheduleResult = await scheduleResponse.json();
        console.log('📦 Schedule API Response:', scheduleResult);

        if (scheduleResult.success && scheduleResult.data) {
          console.log('✅ Processing timetable data...');
          console.log('📋 Raw timetable data:', scheduleResult.data);
          setTimetableData(scheduleResult.data);

          // Set default selected day to first available day with classes
          const daysWithClasses = Object.keys(scheduleResult.data).filter(day =>
            scheduleResult.data[day] && scheduleResult.data[day].length > 0
          );
          if (daysWithClasses.length > 0 && !daysWithClasses.includes(selectedDay)) {
            console.log('📱 Setting default day to:', daysWithClasses[0]);
            setSelectedDay(daysWithClasses[0]);
          }

          console.log('✅ Timetable data updated successfully');
        } else {
          console.log('❌ API returned error:', scheduleResult.message);
          setError(scheduleResult.message || 'Failed to fetch timetable data');
        }

      } catch (error) {
        console.error('💥 Error fetching timetable data:', error);

        // Handle specific errors
        if (error.name === 'SyntaxError' && error.message.includes('Unexpected token')) {
          setError('Server returned HTML instead of JSON. Please check if the API endpoint exists.');
        } else if (error.message.includes('fetch')) {
          setError('Cannot connect to server. Please ensure the server is running.');
        } else {
          setError(`Failed to load timetable data: ${error.message}`);
        }

        // Fallback to userInfo if available
        if (userInfo) {
          console.log('🔄 Using fallback userInfo data');
          setTeacherData(prev => ({
            ...prev,
            name: userInfo.name || prev.name,
            teacherId: userInfo.id || prev.teacherId,
            designation: userInfo.designation || prev.designation
          }));
        }
      } finally {
        setLoading(false);
        // Add fade in effect after loading
        setTimeout(() => setFadeIn(true), 100);
      }
    };

    // Add a small delay to ensure component is mounted
    const timer = setTimeout(fetchTimetableData, 500);
    return () => clearTimeout(timer);
  }, [userInfo]);

  // Handle logout
  const handleLogout = () => {
    console.log('🚪 Logging out...');

    sessionStorage.clear();
    localStorage.clear();

    // Make logout API call
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    if (token) {
      fetch(`${API_LINK}/api/auth/logout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }).catch(error => console.error('Logout API error:', error));
    }

    if (onNavigate) {
      onNavigate('home');
    } else {
      window.location.href = '/';
    }
  };



  // Calculate weekly summary from fetched data
  const getWeeklySummary = () => {
    const allClasses = Object.values(timetableData).flat();
    const uniqueSubjects = [...new Set(allClasses.map(item => item.subject))];
    const uniqueBatches = [...new Set(allClasses.map(item => item.batch))];

    return {
      totalClasses: allClasses.length,
      subjects: uniqueSubjects.length,
      batches: uniqueBatches.length
    };
  };

  // Loading screen
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f4f7fa',
        flexDirection: 'column',
        gap: '1rem'
      }}>
        <div style={{
          width: '50px',
          height: '50px',
          border: '4px solid #e3f2fd',
          borderTop: '4px solid #238548',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <p style={{ color: '#666', fontSize: '1.1rem' }}>Loading your timetable...</p>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  // Error Component
  if (error) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f4f7fa',
        flexDirection: 'column',
        gap: '1rem',
        padding: '2rem'
      }}>
        <div style={{
          backgroundColor: '#ffffff',
          padding: '3rem',
          borderRadius: '12px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
          textAlign: 'center',
          maxWidth: '400px',
          border: '1px solid #e0e0e0'
        }}>
          <div style={{
            fontSize: '4rem',
            color: '#ff6b6b',
            marginBottom: '1.5rem'
          }}>
            ⚠️
          </div>

          <h3 style={{
            color: '#333',
            margin: '0 0 1rem 0',
            fontSize: '1.4rem',
            fontWeight: 600
          }}>
            Unable to Load Timetable
          </h3>

          <p style={{
            color: '#666',
            margin: '0 0 2rem 0',
            fontSize: '1rem',
            lineHeight: 1.5
          }}>
            {error}
          </p>

          <div style={{
            display: 'flex',
            gap: '1rem',
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            <button
              onClick={() => window.location.reload()}
              style={{
                backgroundColor: '#238548',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 500,
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#1e7139';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#238548';
              }}
            >
              Try Again
            </button>

            <button
              onClick={handleLogout}
              style={{
                backgroundColor: '#666',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 500,
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#555';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#666';
              }}
            >
              Logout
            </button>
          </div>
        </div>
      </div>
    );
  }

  const weeklySummary = getWeeklySummary();

  // Handle Take Attendance button click
  const handleTakeAttendance = (classInfo) => {
    // Store class info for the attendance page
    sessionStorage.setItem('selected_class_info', JSON.stringify({
      subject: classInfo.subject,
      batch: classInfo.batch,
      location: classInfo.location,
      timeSlot: classInfo.timeSlot,
      day: classInfo.day || selectedDay
    }));

    // Navigate to attendance page
    onNavigate('teacher-attendance');
  };

  // Improved function to get class for a specific time slot and day
  const getClassForSlot = (timeSlot, day) => {
    const dayClasses = timetableData[day] || [];

    // First try exact match
    let classInfo = dayClasses.find(classInfo => classInfo.timeSlot === timeSlot);

    // If no exact match, try normalized comparison
    if (!classInfo) {
      const normalizedTarget = normalizeTimeSlot(timeSlot);
      classInfo = dayClasses.find(classInfo =>
        normalizeTimeSlot(classInfo.timeSlot) === normalizedTarget
      );
    }

    // If still no match, try flexible matching (remove spaces, colons, etc.)
    if (!classInfo) {
      const flexibleTarget = timeSlot.replace(/[\s:-]/g, '').toLowerCase();
      classInfo = dayClasses.find(classInfo =>
        classInfo.timeSlot.replace(/[\s:-]/g, '').toLowerCase().includes(flexibleTarget.substring(0, 4))
      );
    }

    // Debug logging - only show when there are classes but no match
    if (dayClasses.length > 0 && !classInfo) {
      console.log(`🔍 Looking for time slot "${timeSlot}" on ${day}`);
      console.log(`📋 Available classes:`, dayClasses.map(c => `${c.timeSlot} (${c.subject})`));
      console.log(`❌ No match found for: "${timeSlot}"`);
    }

    return classInfo;
  };

  // Get classes for selected day (mobile) - improved with case-insensitive matching
  const getClassesForDay = (day) => {
    // Try exact match first
    if (timetableData[day]) {
      return timetableData[day];
    }

    // Try case-insensitive matching
    const dayKey = Object.keys(timetableData).find(key =>
      key.toLowerCase() === day.toLowerCase()
    );

    if (dayKey) {
      return timetableData[dayKey];
    }

    // Try partial matching (e.g., "Mon" matches "Monday")
    const partialKey = Object.keys(timetableData).find(key =>
      key.toLowerCase().includes(day.toLowerCase().substring(0, 3)) ||
      day.toLowerCase().includes(key.toLowerCase().substring(0, 3))
    );

    if (partialKey) {
      return timetableData[partialKey];
    }

    // Debug logging
    console.log(`🔍 No classes found for ${day}. Available days:`, Object.keys(timetableData));

    return [];
  };

  // Loading screen
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f4f7fa',
        flexDirection: 'column',
        gap: '1rem'
      }}>
        <div style={{
          width: '50px',
          height: '50px',
          border: '4px solid #e3f2fd',
          borderTop: '4px solid #238548',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <p style={{ color: '#666', fontSize: '1.1rem' }}>Loading your timetable...</p>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  // Error Component
  if (error) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f4f7fa',
        flexDirection: 'column',
        gap: '1rem',
        padding: '2rem'
      }}>
        <div style={{
          backgroundColor: '#ffffff',
          padding: '3rem',
          borderRadius: '12px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
          textAlign: 'center',
          maxWidth: '400px',
          border: '1px solid #e0e0e0'
        }}>
          <div style={{
            fontSize: '4rem',
            color: '#ff6b6b',
            marginBottom: '1.5rem'
          }}>
            ⚠️
          </div>

          <h3 style={{
            color: '#333',
            margin: '0 0 1rem 0',
            fontSize: '1.4rem',
            fontWeight: 600
          }}>
            Unable to Load Timetable
          </h3>

          <p style={{
            color: '#666',
            margin: '0 0 2rem 0',
            fontSize: '1rem',
            lineHeight: 1.5
          }}>
            {error}
          </p>

          <div style={{
            display: 'flex',
            gap: '1rem',
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            <button
              onClick={() => window.location.reload()}
              style={{
                backgroundColor: '#238548',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 500,
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#1e7139';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#238548';
              }}
            >
              Try Again
            </button>

            <button
              onClick={handleLogout}
              style={{
                backgroundColor: '#666',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 500,
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#555';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#666';
              }}
            >
              Logout
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Layout role="teacher" onNavigate={onNavigate} activePage="teacher-timetable" userInfo={userInfo}>
      {/* Timetable-specific CSS */}
      <style>
        {`
          .timetable-container {
            opacity: ${fadeIn ? 1 : 0};
            transition: opacity 0.5s ease-in-out;
          }

          .class-block {
            background: linear-gradient(135deg, #238548 0%, #97d6ac 100%);
            color: white;
            padding: 1rem;
            border-radius: 8px;
            text-align: center;
            height: 100%;
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            transition: all 0.3s ease;
            cursor: pointer;
            min-height: 120px;
          }

          .class-block:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(35, 133, 72, 0.3);
          }

          .class-block h4 {
            margin: 0 0 0.5rem 0;
            font-size: 1rem;
            font-weight: 600;
          }

          .class-block p {
            margin: 0.25rem 0;
            font-size: 0.8rem;
            opacity: 0.9;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 0.5rem;
          }

          .class-block .btn {
            background: rgba(255, 255, 255, 0.2);
            color: white;
            border: 1px solid rgba(255, 255, 255, 0.3);
            padding: 0.5rem 0.75rem;
            border-radius: 4px;
            text-decoration: none;
            font-size: 0.8rem;
            font-weight: 500;
            margin-top: 0.5rem;
            transition: all 0.3s ease;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: 0.5rem;
          }

          .class-block .btn:hover {
            background: rgba(255, 255, 255, 0.3);
            transform: translateY(-1px);
          }

          /* Mobile-specific styles */
          .mobile-class-card {
            background: linear-gradient(135deg, #238548 0%, #97d6ac 100%);
            color: white;
            padding: 0.8rem;
            border-radius: 8px;
            margin-bottom: 0.75rem;
            box-shadow: 0 4px 15px rgba(35, 133, 72, 0.2);
            transition: all 0.3s ease;
          }

          .mobile-class-card:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(35, 133, 72, 0.3);
          }

          .mobile-class-card h3 {
            margin: 0 0 0.5rem 0;
            font-size: 0.95rem;
            font-weight: 600;
          }

          .mobile-class-info {
            display: flex;
            flex-direction: column;
            gap: 0.25rem;
            margin-bottom: 0.75rem;
          }

          .mobile-class-info-item {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            font-size: 0.78rem;
          }

          .mobile-attendance-btn {
            background: rgba(255, 255, 255, 0.2);
            color: white;
            border: 1px solid rgba(255, 255, 255, 0.3);
            padding: 0.5rem 0.75rem;
            border-radius: 6px;
            font-size: 0.8rem;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.3s ease;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 0.5rem;
            width: 100%;
          }

          .mobile-attendance-btn:hover {
            background: rgba(255, 255, 255, 0.3);
            transform: translateY(-1px);
          }

          .day-selector {
            display: flex;
            overflow-x: auto;
            gap: 0.5rem;
            padding: 0.25rem 0;
            margin-bottom: 0.75rem;
            scrollbar-width: none;
            -ms-overflow-style: none;
          }

          .day-selector::-webkit-scrollbar {
            display: none;
          }

          .day-button {
            flex: 0 0 auto;
            padding: 0.4rem 0.8rem;
            border: none;
            border-radius: 25px;
            font-size: 0.8rem;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.3s ease;
            white-space: nowrap;
            min-width: 70px;
          }

          .day-button.active {
            background: #238548;
            color: white;
          }

          .day-button:not(.active) {
            background: #f8f9fa;
            color: #333;
          }

          .day-button:not(.active):hover {
            background: #e9ecef;
          }

          @media (max-width: 768px) {
            .desktop-timetable {
              display: none !important;
            }
            
            .mobile-view {
              display: block !important;
            }
          }

          @media (min-width: 769px) {
            .desktop-timetable {
              display: block !important;
            }
            
            .mobile-view {
              display: none !important;
            }
          }
        `}
      </style>
      {/* Timetable Container */}
      <div className="timetable-container" style={{
        background: '#ffffff',
        borderRadius: '12px',
        boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
        overflow: 'hidden'
      }}>
        <div style={{
          background: '#238548',
          color: '#ffffff',
          padding: isMobile ? '0.8rem 1rem' : '1.5rem',
          display: 'flex',
          alignItems: 'center',
          gap: isMobile ? '0.5rem' : '1rem'
        }}>
          <span style={{ fontSize: isMobile ? '1.3rem' : '1.8rem' }}>📅</span>
          <h3 style={{ margin: 0, fontSize: isMobile ? '1rem' : '1.2rem' }}>Weekly Timetable</h3>
        </div>

        {/* Debug Info */}
        {Object.keys(timetableData).length > 0 && (
          <div style={{
            padding: isMobile ? '8px' : '1rem',
            backgroundColor: '#f8f9fa',
            fontSize: isMobile ? '0.7rem' : '0.8rem',
            color: '#666',
            borderBottom: '1px solid #e9ecef'
          }}>
            📊 Debug: Loaded: {Object.values(timetableData).flat().length} |
            Days: {Object.keys(timetableData).join(', ')} |
            Selected: {selectedDay} ({getClassesForDay(selectedDay).length} classes)
          </div>
        )}

        {/* Mobile View */}
        <div className="mobile-view" style={{ padding: isMobile ? '0.5rem' : '1rem' }}>
          {/* Day Selector */}
          <div className="day-selector">
            {days.map(day => (
              <button
                key={day}
                className={`day-button ${selectedDay === day ? 'active' : ''}`}
                onClick={() => setSelectedDay(day)}
              >
                {day.substring(0, 3)}
              </button>
            ))}
          </div>

          {/* Selected Day Classes */}
          <div>
            <h4 style={{
              margin: isMobile ? '0 0 0.5rem 0' : '0 0 1rem 0',
              color: '#333',
              fontSize: isMobile ? '0.9rem' : '1.1rem',
              textAlign: 'center'
            }}>
              {selectedDay} Classes
            </h4>

            {getClassesForDay(selectedDay).length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '2rem',
                color: '#666',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                border: '2px dashed #dee2e6'
              }}>
                <span style={{ fontSize: '2rem', display: 'block', marginBottom: '0.5rem' }}>📭</span>
                No classes scheduled for {selectedDay}
                <div style={{
                  fontSize: '0.8rem',
                  marginTop: '0.5rem',
                  color: '#999'
                }}>
                  Available days: {Object.keys(timetableData).join(', ') || 'None'}
                </div>
              </div>
            ) : (
              getClassesForDay(selectedDay).map((classInfo, index) => (
                <div key={index} className="mobile-class-card">
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: '0.75rem'
                  }}>
                    <h3>{classInfo.subject}</h3>
                    <div style={{
                      background: 'rgba(255, 255, 255, 0.2)',
                      padding: '0.25rem 0.75rem',
                      borderRadius: '15px',
                      fontSize: '0.8rem',
                      fontWeight: '500'
                    }}>
                      {classInfo.timeSlot}
                    </div>
                  </div>

                  <div className="mobile-class-info">
                    <div className="mobile-class-info-item">
                      <span>📍</span>
                      <span>Location: {classInfo.location}</span>
                    </div>
                    <div className="mobile-class-info-item">
                      <span>👥</span>
                      <span>Batch: {classInfo.batch} ({classInfo.studentCount} Students)</span>
                    </div>
                  </div>

                  <button
                    onClick={() => handleTakeAttendance({ ...classInfo, day: selectedDay })}
                    className="mobile-attendance-btn"
                  >
                    <span>📋</span>
                    Take Attendance
                  </button>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Desktop View - Card Layout */}
        <div className="desktop-timetable" style={{
          padding: '1.5rem'
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
            gap: '1.5rem'
          }}>
            {days.map(day => {
              const dayClasses = getClassesForDay(day);
              return (
                <div key={day} style={{
                  border: '1px solid #e9ecef',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                  transition: 'all 0.3s ease'
                }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-5px)';
                    e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.1)';
                  }}>
                  {/* Day Header */}
                  <div style={{
                    background: 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
                    color: 'white',
                    padding: '1.5rem',
                    textAlign: 'center',
                    position: 'relative'
                  }}>
                    <h3 style={{
                      margin: 0,
                      fontSize: '1.3rem',
                      fontWeight: '600'
                    }}>
                      {day}
                    </h3>
                    <div style={{
                      position: 'absolute',
                      top: '1rem',
                      right: '1rem',
                      background: 'rgba(255, 255, 255, 0.2)',
                      color: 'white',
                      padding: '0.25rem 0.75rem',
                      borderRadius: '15px',
                      fontSize: '0.8rem',
                      fontWeight: '500'
                    }}>
                      {dayClasses.length} {dayClasses.length === 1 ? 'Class' : 'Classes'}
                    </div>
                  </div>

                  {/* Classes List */}
                  <div style={{ padding: '1.5rem' }}>
                    {dayClasses.length > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        {dayClasses.map((classInfo, index) => (
                          <div key={index} style={{
                            background: 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
                            color: 'white',
                            padding: '1.25rem',
                            borderRadius: '8px',
                            transition: 'all 0.3s ease',
                            cursor: 'pointer'
                          }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = 'translateY(-2px)';
                              e.currentTarget.style.boxShadow = '0 6px 20px rgba(35, 133, 72, 0.3)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = 'translateY(0)';
                              e.currentTarget.style.boxShadow = 'none';
                            }}>
                            {/* Class Header */}
                            <div style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'flex-start',
                              marginBottom: '1rem'
                            }}>
                              <h4 style={{
                                margin: 0,
                                fontSize: '1.1rem',
                                fontWeight: '600'
                              }}>
                                {classInfo.subject}
                              </h4>
                              <div style={{
                                background: 'rgba(255, 255, 255, 0.2)',
                                padding: '0.25rem 0.75rem',
                                borderRadius: '15px',
                                fontSize: '0.8rem',
                                fontWeight: '500',
                                whiteSpace: 'nowrap'
                              }}>
                                🕒 {classInfo.timeSlot}
                              </div>
                            </div>

                            {/* Class Details */}
                            <div style={{
                              display: 'grid',
                              gridTemplateColumns: '1fr 1fr',
                              gap: '0.75rem',
                              marginBottom: '1rem',
                              fontSize: '0.9rem',
                              opacity: 0.9
                            }}>
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem'
                              }}>
                                <span>📍</span>
                                <span>{classInfo.location}</span>
                              </div>
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem'
                              }}>
                                <span>👥</span>
                                <span>{classInfo.batch}</span>
                              </div>
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                gridColumn: '1 / -1'
                              }}>
                                <span>🎓</span>
                                <span>{classInfo.studentCount} Students Enrolled</span>
                              </div>
                            </div>

                            {/* Take Attendance Button */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleTakeAttendance({ ...classInfo, day });
                              }}
                              style={{
                                background: 'rgba(255, 255, 255, 0.2)',
                                color: 'white',
                                border: '1px solid rgba(255, 255, 255, 0.3)',
                                padding: '0.75rem 1rem',
                                borderRadius: '6px',
                                fontSize: '0.9rem',
                                fontWeight: '500',
                                cursor: 'pointer',
                                transition: 'all 0.3s ease',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '0.5rem',
                                width: '100%'
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.background = 'rgba(255, 255, 255, 0.3)';
                                e.target.style.transform = 'translateY(-1px)';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                                e.target.style.transform = 'translateY(0)';
                              }}
                            >
                              <span>📋</span>
                              Take Attendance
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{
                        textAlign: 'center',
                        color: '#666',
                        padding: '2rem 1rem',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '8px',
                        border: '2px dashed #dee2e6'
                      }}>
                        <span style={{
                          fontSize: '2.5rem',
                          display: 'block',
                          marginBottom: '0.75rem',
                          opacity: 0.5
                        }}>
                          📭
                        </span>
                        <p style={{
                          margin: 0,
                          fontSize: '1rem',
                          fontWeight: '500'
                        }}>
                          No classes scheduled
                        </p>
                        <p style={{
                          margin: '0.25rem 0 0 0',
                          fontSize: '0.85rem',
                          opacity: 0.7
                        }}>
                          Enjoy your free day!
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Overall Weekly Summary */}
          <div style={{
            marginTop: '2rem',
            background: '#ffffff',
            borderRadius: '12px',
            boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
            overflow: 'hidden'
          }}>
            <div style={{
              background: 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
              color: 'white',
              padding: '1rem 1.5rem',
              display: 'flex',
              alignItems: 'center',
              gap: '1rem'
            }}>
              <span style={{ fontSize: '1.5rem' }}>📊</span>
              <h4 style={{ margin: 0, fontSize: '1.1rem' }}>Weekly Overview</h4>
            </div>
            <div style={{
              padding: '1.5rem',
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '1rem'
            }}>
              {(() => {
                const totalClasses = Object.values(timetableData).flat().length;
                const daysWithClasses = Object.keys(timetableData).filter(day =>
                  timetableData[day] && timetableData[day].length > 0
                ).length;
                const avgClassesPerDay = daysWithClasses > 0 ? (totalClasses / daysWithClasses).toFixed(1) : 0;

                return [
                  { label: 'Total Classes', value: totalClasses, icon: '📚', color: '#238548' },
                  { label: 'Active Days', value: daysWithClasses, icon: '📅', color: '#17a2b8' },
                  { label: 'Avg Classes/Day', value: avgClassesPerDay, icon: '📊', color: '#ffc107' },
                  { label: 'Free Days', value: 6 - daysWithClasses, icon: '🏖️', color: '#6c757d' }
                ].map((stat, index) => (
                  <div key={index} style={{
                    textAlign: 'center',
                    padding: '1rem',
                    background: '#f8f9fa',
                    borderRadius: '8px',
                    transition: 'all 0.3s ease'
                  }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#e9ecef';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#f8f9fa';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}>
                    <div style={{
                      fontSize: '2rem',
                      marginBottom: '0.5rem'
                    }}>
                      {stat.icon}
                    </div>
                    <div style={{
                      fontSize: '1.8rem',
                      fontWeight: 'bold',
                      color: stat.color,
                      marginBottom: '0.25rem'
                    }}>
                      {stat.value}
                    </div>
                    <div style={{
                      fontSize: '0.9rem',
                      color: '#666',
                      fontWeight: '500'
                    }}>
                      {stat.label}
                    </div>
                  </div>
                ));
              })()}
            </div>
          </div>
        </div>
      </div>

      {/* Additional Info */}
      <div style={{
        marginTop: isMobile ? '1rem' : '2rem',
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: isMobile ? '1rem' : '1.5rem'
      }}>
        {/* Weekly Summary */}
        <div style={{
          background: '#ffffff',
          borderRadius: '12px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}>
          <div style={{
            background: '#238548',
            color: '#ffffff',
            padding: isMobile ? '8px 12px' : '1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem'
          }}>
            <span style={{ fontSize: isMobile ? '1.2rem' : '1.5rem' }}>📊</span>
            <h4 style={{ margin: 0, fontSize: isMobile ? '0.9rem' : '1rem' }}>Weekly Summary</h4>
          </div>
          <div style={{ padding: isMobile ? '8px 10px' : '1rem' }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: isMobile ? '0.35rem 0' : '0.5rem 0',
              borderBottom: '1px solid #f0f0f0',
              fontSize: isMobile ? '0.8rem' : '0.9rem'
            }}>
              <span style={{ color: '#666' }}>Total Classes:</span>
              <span style={{ fontWeight: '600', color: '#238548' }}>{weeklySummary.totalClasses}</span>
            </div>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: isMobile ? '0.35rem 0' : '0.5rem 0',
              borderBottom: '1px solid #f0f0f0',
              fontSize: isMobile ? '0.8rem' : '0.9rem'
            }}>
              <span style={{ color: '#666' }}>Subjects:</span>
              <span style={{ fontWeight: '600', color: '#238548' }}>
                {weeklySummary.subjects}
              </span>
            </div>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: isMobile ? '0.35rem 0' : '0.5rem 0',
              fontSize: isMobile ? '0.8rem' : '0.9rem'
            }}>
              <span style={{ color: '#666' }}>Batches:</span>
              <span style={{ fontWeight: '600', color: '#238548' }}>
                {weeklySummary.batches}
              </span>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div style={{
          background: '#ffffff',
          borderRadius: '12px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}>
          <div style={{
            background: '#238548',
            color: '#ffffff',
            padding: isMobile ? '8px 12px' : '1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem'
          }}>
            <span style={{ fontSize: isMobile ? '1.2rem' : '1.5rem' }}>⚡</span>
            <h4 style={{ margin: 0, fontSize: isMobile ? '0.9rem' : '1rem' }}>Quick Actions</h4>
          </div>
          <div style={{ padding: isMobile ? '8px 10px' : '1rem', display: 'flex', flexDirection: 'column', gap: isMobile ? '0.5rem' : '0.75rem' }}>
            <button
              onClick={() => onNavigate('teacher-attendance')}
              style={{
                background: '#238548',
                color: 'white',
                border: 'none',
                padding: isMobile ? '8px 12px' : '0.75rem 1rem',
                borderRadius: '6px',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontSize: isMobile ? '0.8rem' : '0.9rem',
                fontWeight: '500'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = '#1e7139';
                e.target.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = '#238548';
                e.target.style.transform = 'translateY(0)';
              }}
            >
              <span>✅</span>
              Take Attendance
            </button>
            <button
              onClick={() => onNavigate('teacher-profile')}
              style={{
                background: '#f8f9fa',
                color: '#333',
                border: '1px solid #e9ecef',
                padding: isMobile ? '8px 12px' : '0.75rem 1rem',
                borderRadius: '6px',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontSize: isMobile ? '0.8rem' : '0.9rem',
                fontWeight: '500'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = '#e9ecef';
                e.target.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = '#f8f9fa';
                e.target.style.transform = 'translateY(0)';
              }}
            >
              <span>👤</span>
              View Profile
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default TeacherTimetable;